import { QueryClient } from "@tanstack/react-query";
import { httpClient, HttpError } from "@coworker/http-client";
import {
  Article,
  Articles,
  ArticleWithMediaCount,
  EdsData,
  PartialArticle,
  SalesStopData,
} from "../types/article";
import { Ids } from "../types/images";
import { QueryKeys } from "../hooks/queryKeys";
import {
  ImageUsageEntry,
  ImageUsageByMediaResponse,
} from "../types/imageUsage";

export const getArticleById = async (articleId: string) => {
  try {
    return await httpClient.roomsettings.get<Article>(`/articles/${articleId}`);
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const updateArticle = async (
  articleId: string,
  article: PartialArticle
) => {
  await httpClient.roomsettings.put(`/articles/${articleId}`, {
    body: article,
  });
};

export const addArticle = async (
  article: Partial<Article>,
  queryClient?: QueryClient
) => {
  delete article.id;
  const data = await httpClient.roomsettings.post<Ids>("/articles/single", {
    body: article,
  });
  await invalidateArticlesRelatedQueries(queryClient);
  return data;
};

export const addArticlesBulk = async (
  articles: Partial<Article>[],
  groupId: string,
  queryClient?: QueryClient
) => {
  articles.forEach((article) => {
    delete article.id;
  });
  const data = await httpClient.roomsettings.post<Articles>(
    `/articles${groupId ? `?groupId=${groupId}` : ""}`,
    { body: articles }
  );
  await invalidateArticlesRelatedQueries(queryClient);
  return data;
};

export const deleteArticleById = async (
  articleId: string,
  queryClient: QueryClient
) => {
  await httpClient.roomsettings.delete(`/articles/${articleId}`);
  await invalidateArticlesRelatedQueries(queryClient);
};

export const deleteArticleByIds = async (
  articleIds: string[],
  queryClient?: QueryClient
) => {
  await httpClient.roomsettings.delete(
    `/articles?ids=${articleIds.join(",")}`,
    {
      responseType: "text",
    }
  );
  await invalidateArticlesRelatedQueries(queryClient);
};

export const getEDSData = async (countryId: string, articleNumbers: string) => {
  try {
    const data = await httpClient.data.get<EdsData[]>(
      "/eds/getEdsOnArticlesByCountryId",
      {
        params: {
          countryId,
          articleNumbers,
        },
      }
    );
    return data || [];
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getSalesStopData = async (
  storeId: string,
  articleNumbers: string
) => {
  try {
    return await httpClient.data.get<SalesStopData[]>(
      "/sales-stops/getSalesStops",
      {
        params: {
          storeId,
          articleNumbers,
        },
      }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return [];
};

export const searchArticlesInMedias = async (
  storeId: string,
  searchQuery: string,
  hfb?: string
) => {
  try {
    const params: Record<string, string> = {};
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery) params["searchQuery"] = trimmedQuery;
    if (hfb) params["hfb"] = hfb;

    return await httpClient.roomsettings.get<ArticleWithMediaCount[]>(
      `/articles/search/${encodeURIComponent(storeId)}`,
      { params }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getArticlesByHfb = async (storeId: string, hfb: string) => {
  try {
    return await httpClient.roomsettings.get<ArticleWithMediaCount[]>(
      `/articles/hfb/${encodeURIComponent(storeId)}`,
      { params: { hfb } }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return null;
};

export const getCombinationArticleData = async (subArticleId: string) => {
  try {
    return await httpClient.roomsettings.get<Article>(
      `/articles/combinations`,
      { params: { subArticleId } }
    );
  } catch (error) {
    console.error("Fetch error:", error);
  }
  return {};
};

export async function syncCombinationArticles(
  {
    toAdd,
    toUpdate,
    toDelete,
  }: {
    toAdd: Partial<Article>[];
    toUpdate: Article[];
    toDelete: { id: string }[];
  },
  queryClient?: QueryClient
) {
  if (toAdd.length === 0 && toUpdate.length === 0 && toDelete.length === 0) {
    return; // nothing to sync
  }

  const data = await httpClient.roomsettings.post<Articles>(
    "/articles/syncCombinationArticles",
    {
      body: {
        articlesToAdd: toAdd,
        articlesToUpdate: toUpdate,
        articlesToDelete: toDelete,
      },
    }
  );
  await invalidateArticlesRelatedQueries(queryClient);
  return data;
}

async function invalidateArticlesRelatedQueries(queryClient?: QueryClient) {
  if (!queryClient) return;
  await Promise.all([
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Media],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Medias],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.ArticlesByMedia],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.ArticlesByGroup],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.ArticlesByMediaAndProductArticleId],
    }),
    queryClient.invalidateQueries({
      queryKey: [QueryKeys.Notifications],
    }),
  ]);
}

export const getImageUsageByMediaId = async (
  mediaId: string
): Promise<ImageUsageByMediaResponse | null> => {
  type RawImageUsageResponse = {
    containsDeletedItems?: boolean;
    imagesWithDeletedItems?: ImageUsageEntry[];
    message?: string;
    dependentImages?: ImageUsageEntry[];
  };

  const normalize = (
    data: RawImageUsageResponse
  ): ImageUsageByMediaResponse => ({
    ...data,
    containsDeletedItems: !!data.containsDeletedItems,
    imagesWithDeletedItems: data.imagesWithDeletedItems ?? [],
    dependentImages: data.imagesWithDeletedItems ?? data.dependentImages ?? [],
  });

  try {
    const data = await httpClient.roomsettings.get<RawImageUsageResponse>(
      `/images/medias/${mediaId}/check-deleted-items`
    );
    return normalize(data ?? {});
  } catch (error) {
    if (error instanceof HttpError && error.status === 409) {
      return normalize((error.body as RawImageUsageResponse) ?? {});
    }
    console.error("Fetch error:", error);
  }
  return null;
};
